import * as React from "react";

import { ImpactSettingType, SelectType } from "./Enum";
import { IDiagramMappings, IImpactSettings } from "./business-objects/AreaOfDamageOptions";
import AreasOfDamageImpactPoints from "./AreaOfDamageImpactPoints";

const diagramCar = require("./images/DiagramCar.png");
const diagramCarTrailer = require("./images/DiagramCarTrailer.png");
const diagramVan = require("./images/DiagramVan.png");
const diagramTruck = require("./images/DiagramTruck.png");
const diagramMotorCycle = require("./images/DiagramMotorCycle.png");
const diagramFarmVehicle = require("./images/diagramFarmVehicle.png");
const diagramPickUp = require("./images/diagramPickUp.png");
const diagramSchoolBus = require("./images/diagramSchoolBus.png");
const diagramTractorCabOnly = require("./images/diagramTractorCabOnly.png");
const diagramTractorSingleTrailer = require("./images/diagramTractorSingleTrailer.png");
const diagramTruck3OrMoreAxils = require("./images/diagramTruck3OrMoreAxils.png");
const diagramTruckTrailer = require("./images/diagramTruckTrailer.png");
const diagramBus = require("./images/diagramBus.png");

interface IAreasOfDamageStateProps {
  id: string;
  component: any;
  value: string | string[];
  vehicleType: string;
  areaOfDamageBaseSchema: IImpactSettings[];
}

interface IAreasOfDamageActionProps {
  onChange: (value?: string | string[], meta?: { sourceId: string; key?: string }) => void;
}

interface IAreasOfDamageProps extends IAreasOfDamageStateProps, IAreasOfDamageActionProps {}

interface IAreasOfDamageState {
  imageSource: any;
  value: string | string[];
  modifiedAreaOfDamageSchema: IImpactSettings[];
}

export default class AreaOfDamage extends React.Component<IAreasOfDamageProps, IAreasOfDamageState> {
  private input: any;

  // unique instance id to avoid DOM name/id collisions across multiple components
  private static _counter = 0;
  private _instanceId: string;

  private images: { name: string; image: any }[] = [
    { name: "diagramCar", image: diagramCar },
    { name: "diagramCarTrailer", image: diagramCarTrailer },
    { name: "diagramVan", image: diagramVan },
    { name: "diagramTruck", image: diagramTruck },
    { name: "diagramMotorCycle", image: diagramMotorCycle },
    { name: "diagramFarmVehicle", image: diagramFarmVehicle },
    { name: "diagramPickUp", image: diagramPickUp },
    { name: "diagramSchoolBus", image: diagramSchoolBus },
    { name: "diagramTractorCabOnly", image: diagramTractorCabOnly },
    { name: "diagramTractorSingleTrailer", image: diagramTractorSingleTrailer },
    { name: "diagramTruck3OrMoreAxils", image: diagramTruck3OrMoreAxils },
    { name: "diagramTruckTrailer", image: diagramTruckTrailer },
    { name: "diagramBus", image: diagramBus }
  ];

  constructor(props: IAreasOfDamageProps) {
    super(props);
    this._instanceId = `aod_${AreaOfDamage._counter++}`;

    this.state = {
      imageSource: "",
      value: props.value ?? (props.component?.multipleAreas ? [] : ""),
      modifiedAreaOfDamageSchema: this.buildSchemaFromProps(props)
    };
  }

  // ---------- lifecycle ----------
  componentDidMount() {
    this.loadImage(); // only sets imageSource
  }

  componentDidUpdate(prevProps: IAreasOfDamageProps) {
  // ---- sync selected value ONLY when props actually changed ----
  const defPrev = prevProps.component?.multipleAreas ? [] : "";
  const defCurr = this.props.component?.multipleAreas ? [] : "";

  const prevPropsNorm = this.normalizeToArray(prevProps.value ?? defPrev).join("|");
  const currPropsNorm = this.normalizeToArray(this.props.value ?? defCurr).join("|");

  if (prevPropsNorm !== currPropsNorm) {
    // props changed -> reflect into state
    this.setState({ value: this.props.value ?? defCurr });
  }

  // ---- schema rebuild when inputs affecting schema change (same as before) ----
  const schemaInputsChanged =
    prevProps.areaOfDamageBaseSchema !== this.props.areaOfDamageBaseSchema ||
    prevProps.component?.additionalImpactAreas !== this.props.component?.additionalImpactAreas ||
    prevProps.component?.disableImpactAreas !== this.props.component?.disableImpactAreas;

  if (schemaInputsChanged) {
    this.setState({ modifiedAreaOfDamageSchema: this.buildSchemaFromProps(this.props) });
  }

  // ---- image updates when their inputs change (same as before) ----
  const imageInputsChanged =
    prevProps.vehicleType !== this.props.vehicleType ||
    prevProps.component?.diagramMappings !== this.props.component?.diagramMappings;

  if (imageInputsChanged) {
    this.loadImage();
  }
}


  // ---------- helpers ----------
  private normalizeType = (t: any) => {
    const s = String(t).toLowerCase();
    if (s === "checkbox") return ImpactSettingType.checkbox;
    if (s === "area") return ImpactSettingType.area;
    return t;
  };

  private normalizeToArray = (val: string | string[] | undefined): string[] => {
    if (!val) return [];
    return Array.isArray(val) ? val.map(v => String(v)) : [String(val)];
  };

  private buildSchemaFromProps = (props: IAreasOfDamageProps): IImpactSettings[] => {
    const base = (props.areaOfDamageBaseSchema || []).map(a => ({
      ...a,
      type: this.normalizeType(a.type)
    }));

    const extras = (props.component?.additionalImpactAreas || [])
      .map((x: any) => x?.impactAreaContainer)
      .filter(Boolean)
      .map((a: any) => ({ ...a, type: ImpactSettingType.checkbox }));

    // merge by id (extras override base)
    const byId = new Map<string, IImpactSettings>();
    [...base, ...extras].forEach(a => byId.set(String(a.id), a));
    let merged = Array.from(byId.values());

    // apply disables
    const disables = props.component?.disableImpactAreas || [];
    if (disables.length) {
      const disabledIds = new Set(
        disables.map((d: any) => String(d?.disableAreaContainer?.id)).filter(Boolean)
      );
      merged = merged.map(a => ({ ...a, disabled: a.disabled || disabledIds.has(String(a.id)) }));
    }

    return merged;
  };

  private loadImage = () => {
    const diagramMappings: IDiagramMappings[] = this.props.component?.diagramMappings || [];
    const diagram = diagramMappings.find(m => m.values?.some((x: any) => x.value == this.props.vehicleType));
    const image = diagram ? this.images.find(img => img.name == diagram.image)?.image : diagramCar;
    this.setState({ imageSource: image || diagramCar });
  };

  // ---------- events ----------
  onChange = (e: any) => {
    // radio/checkbox path
    if (e?.target) {
      const { type, value, checked } = e.target;
      const id = String(value);

      console.log("onchange got", id);

      if (type === "radio") {
        // single-select: set just the clicked id
        const next = id;
        this.setState({ value: next }, () =>
          this.props.onChange?.(next, { sourceId: this._instanceId, key: this.props.component?.key })
        );
        return;
      }

      if (type === "checkbox") {
        const current = this.normalizeToArray(this.state.value);
        const next = this.props.component?.multipleAreas
          ? (checked ? [...current, id] : current.filter(v => v !== id))
          : (checked ? id : "");
        this.setState({ value: next }, () =>
          this.props.onChange?.(next, { sourceId: this._instanceId, key: this.props.component?.key })
        );
        return;
      }
    }

    // fallback: clicks from image map or other sources pass the id directly
    const id = String(e);
    if (this.props.component?.multipleAreas) {
      const current = this.normalizeToArray(this.state.value);
      const next = current.includes(id) ? current.filter(v => v !== id) : [...current, id];
      this.setState({ value: next }, () =>
        this.props.onChange?.(next, { sourceId: this._instanceId, key: this.props.component?.key })
      );
    } else {
      const next = id;
      this.setState({ value: next }, () =>
        this.props.onChange?.(next, { sourceId: this._instanceId, key: this.props.component?.key })
      );
    }
  };

  // ---------- render ----------
  render() {
    const areasOfImpact: IImpactSettings[] = this.state.modifiedAreaOfDamageSchema || [];
    const isSingle = !this.props.component?.multipleAreas;
    const selectType: SelectType = isSingle ? SelectType.Single : SelectType.Multiple;
    const inputType = isSingle ? "radio" : "checkbox";

    // unique DOM group name/id base per component instance
    const nameBase = `${this.props.component?.key || "areasOfDamage"}_${this._instanceId}`;

    // values for binding
    const valueArr = this.normalizeToArray(this.state.value);
    const singleValue = String(Array.isArray(this.state.value) ? this.state.value[0] ?? "" : this.state.value);

    const checkboxItems = areasOfImpact.filter(s => this.normalizeType(s.type) === ImpactSettingType.checkbox);
    const areaItems = areasOfImpact.filter(s => this.normalizeType(s.type) === ImpactSettingType.area);

    return (
      <div>
        <div className="col">
          {checkboxItems.map((impact: IImpactSettings, index) => (
            <div key={index} style={{ marginLeft: "15px" }}>
              <label className="checkbox" >
                <input
                  disabled={impact.disabled}
                  type={inputType}
                  name={nameBase} // IMPORTANT: radios grouped per instance
                  value={impact.id}
                  checked={
                    isSingle
                      ? singleValue === String(impact.id)
                      : valueArr.includes(String(impact.id))
                  }
                  onChange={this.onChange}
                  id={`${this.props.id || nameBase}_${impact.id}`}
                />
                <span style={{paddingLeft: "5px"}}>{impact.value}</span>
              </label>
            </div>
          ))}
        </div>

       <AreasOfDamageImpactPoints
          component={this.props.component}
          impactSettings={areaItems}
          selectType={selectType}
          value={this.state.value}      // raw: string for single, array for multi
          imageSrc={this.state.imageSource}
          inputChanged={this.onChange}
          idPrefix={nameBase}
        />
      </div>
    );
  }
}
